import { useMembers } from "../member/useMembers";
import { useUserAccountRole } from "../permissions/useUserAccountRole";
import { Authentication } from "./useQueryAuthentications";

interface Input {
  authentications: Authentication[];
}

/** This hook takes an array of authentications and hydrates them with
 * a member object representing the owner of the authentication.
 * This object is used for rendering owner name and info when displaying access.
 */

export function useHydrateAuthenticationsWithOwners({
  authentications,
}: Input) {
  const { isAccountOwner, isSuperAdmin } = useUserAccountRole();

  // Build a unique list of ownersIds referenced in the list
  const ownerIds = authentications.reduce((accumulator, auth) => {
    if (auth.customuserId && !auth.isManaged) {
      accumulator.add(auth.customuserId);
    }
    return accumulator;
  }, new Set<number>());

  // membership_status=all requires owner/super_admin permissions on the
  // monolith members endpoint; fall back to active-only for other roles
  const { members, isLoading } = useMembers({
    ids: [...ownerIds],
    ...(isAccountOwner || isSuperAdmin ? { membershipStatus: "all" } : {}),
  });

  // Hydrate authentications with implementation objects
  const hydratedAuths = authentications.map((auth) => {
    const ownerId = auth.customuserId;
    return {
      ...auth,
      owner: ownerId && !auth.isManaged ? members[ownerId] : undefined,
    };
  });

  return {
    authentications: hydratedAuths,
    isLoading,
  };
}
